import { useEffect, useRef, useState } from 'react';
import { IonModal, createGesture } from '@ionic/react';
import { PhotoViewerControls } from './PhotoViewerControls';
import { usePhotoNavigation } from './usePhotoNavigation';

// Horizontal distance (px) a swipe must travel to change photo
const SWIPE_DISTANCE = 50;

export interface ViewerPhoto {
  id: string;
  url: string;
}

interface PhotoViewerModalProps {
  photos: ViewerPhoto[];
  /** Photo shown when the viewer opens. */
  initialIndex: number;
  /** Called once the viewer has closed (after its animation). */
  onClose: () => void;
}

/**
 * Full-screen viewer over a translucent black backdrop. The photo is fitted to the
 * screen, and the user moves between photos by swiping, with the chevrons or, on
 * the web, with the arrow keys (Esc closes).
 *
 * It opens when mounted: render it only while it is shown, and unmount it from
 * `onClose`. Each opening is then a fresh mount that starts at `initialIndex`.
 */
export const PhotoViewerModal = ({ photos, initialIndex, onClose }: PhotoViewerModalProps) => {
  const { index, next, prev } = usePhotoNavigation(photos.length, initialIndex);
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const modal = useRef<HTMLIonModalElement>(null);
  // Dismissing (instead of unmounting right away) keeps the closing animation
  const close = () => void modal.current?.dismiss();

  useEffect(() => {
    if (!container) return;
    const gesture = createGesture({
      el: container,
      gestureName: 'photo-viewer-swipe',
      direction: 'x',
      threshold: 10,
      onEnd: ({ deltaX }) => {
        if (deltaX <= -SWIPE_DISTANCE) next();
        else if (deltaX >= SWIPE_DISTANCE) prev();
      },
    });
    gesture.enable();
    return () => gesture.destroy();
  }, [container, next, prev]);

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight') next();
      else if (event.key === 'ArrowLeft') prev();
      else if (event.key === 'Escape') void modal.current?.dismiss();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [next, prev]);

  const photo = photos[index];

  return (
    <IonModal
      ref={modal}
      isOpen
      onDidDismiss={onClose}
      style={
        {
          '--width': '100%',
          '--height': '100%',
          '--border-radius': '0',
          '--background': 'rgba(0, 0, 0, 0.9)',
        } as React.CSSProperties
      }
    >
      <div
        ref={setContainer}
        className="relative flex h-full w-full items-center justify-center"
        style={{ background: 'rgba(0, 0, 0, 0.9)' }}
        data-testid="photo-viewer"
      >
        {photo && (
          <img
            src={photo.url}
            alt={`Foto ${index + 1} de ${photos.length}`}
            className="max-h-full max-w-full select-none object-contain"
            draggable={false}
          />
        )}
        <PhotoViewerControls
          index={index}
          count={photos.length}
          onClose={close}
          onPrev={prev}
          onNext={next}
        />
      </div>
    </IonModal>
  );
};
